import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Crown, CheckCircle2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", price: 10000, sessions: "", wallet_value: 11500, description: "", bottom_text: "", is_popular: false, benefits: [], status: "active" };

export default function AdminMemberships() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/memberships");
      setList(r.data);
    } catch {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        wallet_value: Number(form.wallet_value || 0),
        benefits: form.benefits.filter(Boolean),
      };
      if (editing) {
        await api.put(`/admin/memberships/${editing}`, payload);
      } else {
        await api.post("/admin/memberships", payload);
      }
      toast.success("Plan saved successfully");
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete plan?")) return;
    try {
      await api.delete(`/admin/memberships/${id}`);
      toast.success("Plan deleted successfully");
      load();
    } catch {
      toast.error("Failed to delete plan");
    }
  };

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...form.benefits];
    newBenefits[index] = value;
    setForm({ ...form, benefits: newBenefits });
  };

  const addBenefit = () => {
    setForm({ ...form, benefits: [...form.benefits, ""] });
  };

  const removeBenefit = (index) => {
    const newBenefits = form.benefits.filter((_, i) => i !== index);
    setForm({ ...form, benefits: newBenefits });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Memberships...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-memberships" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
            <Crown size={16} /> MASTER · MEMBERSHIP PROGRAM
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Membership Plans
          </h1>
        </div>

        <button
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          data-testid="plan-add-btn"
          className="gold-btn rounded-xl py-3 px-6 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2"
        >
          <Plus size={16} /> Add New Plan
        </button>
      </motion.div>

      {/* Grid of Plans */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {list.map((p) => (
          <div
            key={p.id}
            className={`rounded-2xl border p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 flex flex-col justify-between space-y-5 hover:scale-[1.02] transition-all relative overflow-hidden ${
              p.is_popular ? "border-amber-400/90 shadow-amber-950/40" : "border-white/15"
            }`}
          >
            {p.is_popular && (
              <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-amber-300 text-black text-[10px] font-extrabold px-3 py-1 rounded-bl-xl uppercase tracking-widest flex items-center gap-1 shadow-md">
                <Sparkles size={11} /> Popular Plan
              </div>
            )}

            <div className="space-y-4">
              <div className="flex justify-between items-start pt-2">
                <div>
                  <h3 className="font-heading text-3xl font-bold text-white">{p.name}</h3>
                  <div className="text-amber-300 font-bold text-lg mt-1">
                    Pay: {inr(p.price)} <span className="text-white/40">|</span> Get: {inr(p.wallet_value || 0)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditing(p.id); setForm({ ...empty, ...p }); setOpen(true); }}
                    className="p-2 rounded-xl border border-white/20 bg-white/5 hover:border-amber-400 text-white/70 hover:text-amber-300 transition-all"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => del(p.id)}
                    className="p-2 rounded-xl border border-red-500/40 bg-red-950/20 hover:bg-red-900/40 text-red-300 transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {p.description && (
                <p className="text-xs text-white/70 italic leading-relaxed border-t border-b border-white/10 py-3">
                  "{p.description}"
                </p>
              )}

              <div className="space-y-2">
                <div className="text-[10px] text-amber-400 uppercase tracking-widest font-extrabold">INCLUDED BENEFITS:</div>
                <ul className="space-y-2 text-xs text-white/80 font-medium">
                  {p.benefits?.map((it, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-xs text-white/40 pt-4 border-t border-white/10 flex justify-between items-center font-semibold">
              <span>Status: <strong className="text-emerald-400 capitalize">{p.status}</strong></span>
              {p.bottom_text && <span className="text-[10px] text-amber-300/80 italic truncate max-w-[170px]">{p.bottom_text}</span>}
            </div>
          </div>
        ))}

        {list.length === 0 && (
          <div className="col-span-full border border-white/10 p-16 text-center text-white/50 bg-hpaper rounded-2xl font-medium">
            No membership plans found. Click "Add New Plan" to create one.
          </div>
        )}
      </motion.div>

      {/* Dialog Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0c0c0c] border border-amber-400/80 rounded-2xl text-white max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 lg:p-8">
          <DialogHeader className="border-b border-white/10 pb-4">
            <DialogTitle className="font-heading text-3xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
              {editing ? "Edit Membership Plan" : "Add Membership Plan"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Membership Plan Name</label>
              <input
                placeholder="e.g. Silver Membership"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Price / You Pay (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 10000"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold text-amber-400"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Wallet Value / You Receive (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 11500"
                  value={form.wallet_value}
                  onChange={(e) => setForm({ ...form, wallet_value: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold text-emerald-400"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Description</label>
              <textarea
                placeholder="e.g. Entry-level. Get ₹11,500 wallet for ₹10,000 — usable across every branch, every service."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 outline-none rounded-xl resize-none text-sm text-white transition-all"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Bottom Info / Footer Text</label>
              <input
                placeholder="e.g. Non-refundable · Valid 12 months · Usable at all branches"
                value={form.bottom_text}
                onChange={(e) => setForm({ ...form, bottom_text: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl border border-amber-400/30 bg-amber-950/20">
              <input
                type="checkbox"
                id="is_popular"
                checked={form.is_popular}
                onChange={(e) => setForm({ ...form, is_popular: e.target.checked })}
                className="bg-black border border-white/20 focus:border-hgold outline-none accent-amber-400 h-5 w-5 rounded cursor-pointer"
              />
              <label htmlFor="is_popular" className="text-xs font-bold text-amber-300 cursor-pointer select-none">
                Highlight this plan as "Most Popular" (Gold Border)
              </label>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-white/90 block">Benefits & Inclusions</label>
              {form.benefits?.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    placeholder={`Benefit #${index + 1}`}
                    value={benefit}
                    onChange={(e) => handleBenefitChange(index, e.target.value)}
                    className="flex-1 bg-black/70 border border-white/20 focus:border-hgold px-4 py-2.5 text-sm text-white outline-none rounded-xl transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => removeBenefit(index)}
                    className="p-2.5 rounded-xl border border-red-500/40 bg-red-950/20 hover:bg-red-900/40 text-red-300 transition-all"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addBenefit}
                className="w-full py-2.5 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 text-amber-300 text-xs font-bold uppercase tracking-wider transition-all"
              >
                + Add Benefit Line
              </button>
            </div>

            <button
              onClick={save}
              disabled={saving}
              className="gold-btn w-full justify-center py-4 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2 mt-4"
            >
              {saving ? "Saving..." : "Save Membership Plan"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
